// client/src/pages/secure/AgentProductionPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useApiClient } from '../../hooks/useApiClient';
import DataList from '../../components/shared/DataList';

const formatCurrency = (amount) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(amount);

const AgentProductionPage = () => {
    const [rows, setRows] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const apiClient = useApiClient();

    const fetchReport = useCallback(() => {
        setIsLoading(true);
        apiClient.get('/api/reports/agent-production')
            .then(data => setRows(data))
            .catch(err => setError(err.message || 'Failed to load production report.'))
            .finally(() => setIsLoading(false));
    }, [apiClient]);

    useEffect(() => {
        fetchReport();
    }, [fetchReport]);

    if (isLoading) return <div className="p-8">Loading production report...</div>;
    if (error) return <div className="p-8 text-red-500">{error}</div>;

    const totals = rows.reduce((acc, r) => ({
        closed_count: acc.closed_count + Number(r.closed_count),
        closed_volume: acc.closed_volume + Number(r.closed_volume),
        gci: acc.gci + Number(r.gci),
        pipeline_count: acc.pipeline_count + Number(r.pipeline_count),
        pipeline_volume: acc.pipeline_volume + Number(r.pipeline_volume),
    }), { closed_count: 0, closed_volume: 0, gci: 0, pipeline_count: 0, pipeline_volume: 0 });

    return (
        <div className="p-4 md:p-8">
            <h1 className="text-3xl font-bold text-slate-800 mb-2">Agent Production</h1>
            <p className="text-slate-500 mb-6">All-time closed volume, gross commission income, and active pipeline, per agent.</p>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-white p-4 rounded-lg shadow">
                    <p className="text-sm text-slate-500">Total Closed Volume</p>
                    <p className="text-2xl font-bold text-slate-800">{formatCurrency(totals.closed_volume)}</p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow">
                    <p className="text-sm text-slate-500">Total GCI</p>
                    <p className="text-2xl font-bold text-slate-800">{formatCurrency(totals.gci)}</p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow">
                    <p className="text-sm text-slate-500">Closed Deals</p>
                    <p className="text-2xl font-bold text-slate-800">{totals.closed_count}</p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow">
                    <p className="text-sm text-slate-500">Pipeline Volume</p>
                    <p className="text-2xl font-bold text-slate-800">{formatCurrency(totals.pipeline_volume)}</p>
                </div>
            </div>

            <div className="bg-white rounded-lg shadow overflow-x-auto">
                <div className="p-4"><DataList rows={rows} rowKey="agent_id" csvName="agent-production" defaultSort={{ key: 'gci', dir: 'desc' }} empty="No agents on file."
                    filters={[{ key: 'status', label: 'Status' }]}
                    columns={[
                        { key: 'name', label: 'Agent', render: (row) => <><Link to={`/dashboard/agents/${row.agent_id}`} className="text-indigo-600 hover:text-indigo-900 font-medium">{row.name}</Link>
                            {row.status !== 'Active' && <span className="ml-2 text-xs text-slate-400">({row.status})</span>}</> },
                        { key: 'status', label: 'Status' },
                        { key: 'closed_count', label: 'Closed Deals', type: 'number' },
                        { key: 'closed_volume', label: 'Closed Volume', type: 'number', render: (row) => formatCurrency(row.closed_volume) },
                        { key: 'gci', label: 'GCI', type: 'number', render: (row) => <span className="font-semibold text-slate-800">{formatCurrency(row.gci)}</span> },
                        { key: 'pipeline_count', label: 'Pipeline Deals', type: 'number' },
                        { key: 'pipeline_volume', label: 'Pipeline Volume', type: 'number', render: (row) => formatCurrency(row.pipeline_volume) },
                    ]} /></div>
            </div>
        </div>
    );
};

export default AgentProductionPage;
